// src/components/news/NewsArticleCard.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import piNetworkLogo from '../../assets/pinetworklogo-icon.jpg';
import newsGoldTickIcon from '../../assets/newsgoldtick-icon.png';

import './NewsArticleCard.css';

function formatPublishedDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (
    Number.isNaN(date.getTime())
  ) {
    return '';
  }

  try {
    return new Intl.DateTimeFormat(
      undefined,
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }
    ).format(date);
  } catch {
    return date.toLocaleDateString();
  }
}

function NewsArticleCard({
  article,
}) {
  const navigate = useNavigate();

  const [
    imageFailed,
    setImageFailed,
  ] = useState(false);

  if (!article) {
    return null;
  }

  const articleId =
    article._id ||
    '';

  const title =
    article.title ||
    'Pi Network update';

  const sourceName =
    article.sourceName ||
    'Pi Network';

  const excerpt =
    article.excerpt ||
    'Read the latest official Pi Network update.';

  const publishedLabel =
    formatPublishedDate(
      article.publishedAt
    );

  const categories = Array.isArray(
    article.categories
  )
    ? article.categories
        .filter(Boolean)
        .slice(0, 2)
    : [];

  const hasImage =
    Boolean(article.imageUrl) &&
    !imageFailed;

  const canOpenArticle =
    Boolean(articleId);

  const handleOpenArticle = () => {
    if (!articleId) {
      return;
    }

    navigate(
      `/news/${articleId}`,
      {
        state: {
          article,
        },
      }
    );
  };

  const handleKeyDown = (event) => {
    if (
      event.key === 'Enter' ||
      event.key === ' '
    ) {
      event.preventDefault();
      handleOpenArticle();
    }
  };

  return (
    <article
      className="news-article-card"
      role={
        canOpenArticle
          ? 'link'
          : undefined
      }
      tabIndex={
        canOpenArticle
          ? 0
          : undefined
      }
      onClick={handleOpenArticle}
      onKeyDown={handleKeyDown}
      aria-label={
        canOpenArticle
          ? `Open discussion for ${title}`
          : undefined
      }
    >
      <div className="news-article-media">
        {hasImage ? (
          <img
            src={article.imageUrl}
            alt=""
            className="news-article-image"
            loading="lazy"
            onError={() =>
              setImageFailed(true)
            }
          />
        ) : (
          <div className="news-article-image-fallback">
            <img
              src={piNetworkLogo}
              alt=""
              className="news-article-fallback-logo"
            />
          </div>
        )}

        <div className="news-article-media-overlay" />

        <div className="news-article-source-badge">
          <span className="news-article-source-mark">
            <img
              src={piNetworkLogo}
              alt=""
              className="news-article-source-logo"
            />
          </span>

          <span className="news-article-source-name">
            {sourceName}
          </span>

          {article.isVerifiedSource && (
            <span
              className="news-article-verified"
              aria-label="Verified official source"
              title="Verified official source"
            >
              <img
                src={newsGoldTickIcon}
                alt=""
                className="news-article-verified-icon"
              />
            </span>
          )}
        </div>
      </div>

      <div className="news-article-body">
        <div className="news-article-meta">
          {publishedLabel && (
            <span>
              {publishedLabel}
            </span>
          )}

          {publishedLabel &&
            categories.length > 0 && (
              <span
                className="news-article-meta-dot"
                aria-hidden="true"
              />
            )}

          {categories.length > 0 && (
            <div className="news-article-categories">
              {categories.map(
                (category) => (
                  <span
                    className="news-article-category"
                    key={`${
                      article._id ||
                      title
                    }-${category}`}
                  >
                    {category}
                  </span>
                )
              )}
            </div>
          )}
        </div>

        <h2 className="news-article-title">
          {title}
        </h2>

        <p className="news-article-excerpt">
          {excerpt}
        </p>

        <div className="news-article-footer">
          <span className="news-article-read-label">
            Read &amp; discuss
          </span>

          <span
            className="news-article-arrow"
            aria-hidden="true"
          >
            <span className="news-article-arrow-symbol">
              →
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}

export default NewsArticleCard;
